import React, { useMemo } from 'react';
import {
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  CondensedHeader,
  useApi,
  PluralizeUtil,
  CondensedLabel,
  CondensedMedium,
} from '@axiom/ui';
import type { Candidate, CandidateRedux } from '@axiom/validation';

import { CandidateOpportunityLegacyApi } from '../../api/candidate-opportunity-legacy-api';
import { DateUtil } from '../../utils/date-util';

export const TalentProfileDetailAvailability = ({
  candidate,
  name,
}: {
  candidate: CandidateRedux | Candidate;
  name: string;
}) => {
  const [{ data: candidateOpportunities }] = useApi(
    CandidateOpportunityLegacyApi.readCandidateOpportunities(candidate.id)
  );
  const [
    engagedAtLowerEscCount,
    wouldAcceptLowerEscCount,
    wasAskedAboutLowerEscCount,
  ] = useMemo(
    () =>
      candidateOpportunities.reduce(
        (
          [engagedAtLowerEsc, wouldAcceptLowerEsc, wasAskedAboutLowerEsc],
          curr
        ) => {
          return [
            curr.candidateEngagedAtLowerEsc
              ? engagedAtLowerEsc + 1
              : engagedAtLowerEsc,
            curr.openToLowerEsc ? wouldAcceptLowerEsc + 1 : wouldAcceptLowerEsc,
            curr.openToLowerEsc === null
              ? wasAskedAboutLowerEsc
              : wasAskedAboutLowerEsc + 1,
          ];
        },
        [0, 0, 0]
      ),
    [candidateOpportunities]
  );

  const workPreferences = [
    candidate.openToSecondment ? 'Open to Secondments' : null,
    candidate.openToLawFirms ? 'Open to Law Firm clients' : null,
    candidate.openToVariableHourlyProjects
      ? 'Open to Variable Hourly opportunities'
      : null,
    candidate.openToLargeProjects ? 'Open to Projects work' : null,
    candidate.openToPermanentPlacement ? 'Open to Permanent Placement' : null,
    candidate.isOpenToCommute ? 'Open to Onsite' : null,
  ]
    .filter(preference => preference !== null)
    .join(', ');
  return (
    <div data-test={name}>
      <Gutter bottom="16px">
        <CondensedHeader name="AVAILABILITY_PREFERENCES">
          Work and Availability Preferences
        </CondensedHeader>
      </Gutter>
      <Grid columns={2}>
        <GridRow gutterBottom="16px">
          <GridColumn>
            <CondensedLabel>Preferred Weekly Hours Confirmed</CondensedLabel>
            <CondensedMedium name="PREFERRED_WEEKLY_HOURS_CONFIRMED">
              {candidate.availabilityPreferencesUpdatedAt === null
                ? '--'
                : `${DateUtil.getDaysSinceNow(
                    candidate.availabilityPreferencesUpdatedAt
                  )} ${PluralizeUtil({
                    text: 'day',
                    count: DateUtil.getDaysSinceNow(
                      candidate.availabilityPreferencesUpdatedAt
                    ),
                  })} ago`}
            </CondensedMedium>
          </GridColumn>
          <GridColumn>
            <CondensedLabel>Profile Status Last Changed</CondensedLabel>
            <CondensedMedium name="PROFILE_STATUS_LAST_CHANGED">
              {candidate.daysSinceStatusChange === null
                ? '--'
                : `${candidate.daysSinceStatusChange} ${PluralizeUtil({ text: 'day', count: candidate.daysSinceStatusChange })} ago`}
            </CondensedMedium>
          </GridColumn>
        </GridRow>
        <GridRow gutterBottom="16px">
          <GridColumn>
            <CondensedLabel>Preferred Weekly Hours</CondensedLabel>
            <CondensedMedium name="PREFERRED_WEEKLY_HOURS">
              {candidate.desiredWeeklyMaxHours || candidate.maxHours
                ? `${candidate.desiredWeeklyMaxHours ?? candidate.maxHours} ${PluralizeUtil({ text: 'hr', count: candidate.desiredWeeklyMaxHours ?? candidate.maxHours ?? 0 })}`
                : '--'}
            </CondensedMedium>
          </GridColumn>
          <GridColumn>
            <CondensedLabel>Required Minimum Weekly Hours</CondensedLabel>
            <CondensedMedium name="REQUIRED_MINIMUM_WEEKLYHOURS">
              {candidate.minWeeklyHoursPerPosition === null
                ? '--'
                : `${candidate.minWeeklyHoursPerPosition} ${PluralizeUtil({
                    text: 'hr',
                    count: candidate.minWeeklyHoursPerPosition,
                  })}`}
            </CondensedMedium>
          </GridColumn>
        </GridRow>
        <GridRow gutterBottom="16px">
          <GridColumn>
            <CondensedLabel>Additional Client</CondensedLabel>
            <CondensedMedium name="ADDITIONAL_CLIENT">
              {candidate.isOpenToMultipleClients ? 'Yes' : 'No'}
            </CondensedMedium>
          </GridColumn>
          <GridColumn>
            <CondensedLabel>Other Opportunities</CondensedLabel>
            <CondensedMedium name="OTHER_OPPORTUNITIES">
              {candidate.isOpenToOtherWork ? 'Yes' : 'No'}
            </CondensedMedium>
          </GridColumn>
        </GridRow>
        <GridRow gutterBottom="16px">
          <GridColumn>
            <CondensedLabel>Notice Period</CondensedLabel>
            <CondensedMedium name="NOTICE_PERIOD">
              {candidate.noticePeriod || '--'}
            </CondensedMedium>
          </GridColumn>
        </GridRow>
        <GridRow gutterBottom="16px">
          <GridColumn width={12}>
            <CondensedLabel>Work Preferences</CondensedLabel>
            <CondensedMedium name="WORK_PREFERENCES">
              {workPreferences || '--'}
            </CondensedMedium>
          </GridColumn>
        </GridRow>
        <GridRow gutterBottom="16px">
          <GridColumn width={12} name="OPEN_TO_LOWER_ESC">
            <CondensedLabel>Open to Lower ESC</CondensedLabel>
            <CondensedMedium name="OPEN_TO_LOWER_ESC_CONTENT">
              Open to lower ESC:{' '}
              {wasAskedAboutLowerEscCount === 0
                ? `Unknown. ${candidate.calculatedDisplayName} does not have access to opp feed, or they have never expressed interest through opp feed.`
                : `${wouldAcceptLowerEscCount} of ${wasAskedAboutLowerEscCount} EOIs made on opp feed`}{' '}
              Engaged on lower ESC opp: {engagedAtLowerEscCount} time(s)
            </CondensedMedium>
          </GridColumn>
        </GridRow>
      </Grid>
    </div>
  );
};
